import Link from 'next/link';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { Text } from '@/components/@base/typography';
import CategoryItem from '@/components/shared/components/search/result/category';
import Item from '@/components/shared/components/search/result/item';
import SkeletonResults from '@/components/shared/components/search/result/skeleton';
import Props from '@/components/shared/components/search/result/type';
import { useGetSearchWithCategory } from '@/components/shared/services/hooks';

import useCommonModalStore from '@/store/common-modal-store';
import { useSearchStore } from '@/store/search.store';

import {
  DATA_CY_SEARCH_No_RESULT,
  DATA_CY_SEARCH_RESULT_PRODUCTS,
  DATA_CY_SEARCH_RESULT_THEME_DEMOS,
} from '@/constant/cypress';
import localization from '@/constant/localization';

export default function Result({ demos, products, categories }: Props) {
  const { params } = useSearchStore();
  const { data, isRefetching } = useGetSearchWithCategory({});
  const { setShow } = useCommonModalStore();

  const handleCloseModal = () => {
    setShow(false);
  };

  if (!data) {
    return null;
  }
  const TOTAL_ITEMS = 4;

  const promotedProducts = products?.filter((p) => p.promoted) ?? [];
  const nonPromotedProducts = (products ?? []).filter((p) => !p.promoted);
  const hasPromoted = promotedProducts.length > 0;

  let displayProducts: typeof products = [];
  let displayDemos: typeof demos = [];

  if (hasPromoted) {
    const promotedToShow = promotedProducts.slice(0, TOTAL_ITEMS);
    displayProducts = [...promotedToShow];

    if (displayProducts.length < TOTAL_ITEMS && nonPromotedProducts.length > 0) {
      const remaining = TOTAL_ITEMS - displayProducts.length;
      displayProducts = [...displayProducts, ...nonPromotedProducts.slice(0, remaining)];
    }

    if (displayProducts.length < TOTAL_ITEMS) {
      const remaining = TOTAL_ITEMS - displayProducts.length;
      displayDemos = demos?.slice(0, remaining) ?? [];
    }
  } else {
    const maxDemosToShow = Math.min(demos?.length ?? 0, 2);
    const productsToShow = Math.min(TOTAL_ITEMS - maxDemosToShow, products?.length ?? 0);

    displayProducts = products?.slice(0, productsToShow) ?? [];
    displayDemos = demos?.slice(0, maxDemosToShow) ?? [];
  }

  const noResults = !isRefetching && displayProducts.length === 0 && displayDemos.length === 0;

  const showProducts = displayProducts.length > 0 && !noResults;
  const showDemos = displayDemos.length > 0 && !noResults;

  return (
    <Container center className='min-h-[335px] flex-col items-start justify-between px-4 py-4'>
      {isRefetching ? (
        <SkeletonResults />
      ) : (
        <>
          {noResults ? (
            <Container
              data-cy={DATA_CY_SEARCH_No_RESULT}
              center
              className='h-[200px] w-full p-5 text-[#788DAB]'
            >
              <Text>{localization.notFoundAnyResult}</Text>
            </Container>
          ) : (
            <>
              {showProducts && (
                <Container
                  className='w-full border-b! border-[#E1E3E5]'
                  data-cy={DATA_CY_SEARCH_RESULT_PRODUCTS}
                >
                  <Text bold className='-mt-0.5 -mb-1 pr-2 font-[550]! text-[#424244]'>
                    {localization.productSearch}
                  </Text>
                  {displayProducts.map((item, index) => (
                    <Item
                      linkId={`search-result-product-${index + 1}`}
                      className='search-result-product'
                      title={item.titleHighlighted}
                      slug={item.slug}
                      icon={item.icon}
                      key={item.id}
                      demoId={null}
                      promoted={item.promoted}
                    />
                  ))}
                </Container>
              )}

              {showDemos && (
                <Container
                  className='w-full border-b! border-[#E1E3E5]'
                  data-cy={DATA_CY_SEARCH_RESULT_THEME_DEMOS}
                >
                  <Text bold className='-mb-1 pt-3.5 pr-2 text-sm! font-[550]! text-[#424244]'>
                    {localization.searchForTemplateDemos}
                  </Text>
                  {displayDemos.map((item, index) => (
                    <Item
                      linkId={`search-result-demo-${index + 1}`}
                      className='search-result-demo'
                      title={item.titleHighlighted}
                      slug={item.slug}
                      icon={item.icon}
                      key={item.demoId}
                      demoId={item.demoId ?? null}
                    />
                  ))}
                </Container>
              )}

              {categories && categories?.length > 0 && (
                <Container className='-mb-7 w-full pt-3'>
                  {categories
                    ?.slice(0, 3)
                    ?.map((item, index) => (
                      <CategoryItem
                        linkId='search-result-category'
                        find={params}
                        count={item.count}
                        title={item.title}
                        id={item.id}
                        index={index}
                      />
                    ))}
                </Container>
              )}
            </>
          )}
        </>
      )}
      <Button
        id='search-view-results-button'
        className='mt-7 h-[46px] w-full bg-[#F7F8F9] py-3'
      >
        <Link
          href={`/search?find=${params}`}
          onClick={handleCloseModal}
          className='flex'
          prefetch={false}
        >
          <Text className='text-[#878F9B]' bold>
            {localization.seeAllResult}
          </Text>
          <Text bold className='pr-1 text-[#424244]'>
            {params}
          </Text>
        </Link>
      </Button>
    </Container>
  );
}
